import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getMyPromotions,
  getMe,
  getMyCreditRequests,
  createCreditRequest,
  updatePromotion,
  getMyCreditTransactions,
} from '../services/api';
import type { Promotion, CustomerCreditRequest, CustomerCreditTransaction } from '../services/api';
import './CustomerDashboard.css';

const CustomerDashboard: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [credits, setCredits] = useState<number | null>(null);
  const [creditRequests, setCreditRequests] = useState<CustomerCreditRequest[]>([]);
  const [transactions, setTransactions] = useState<CustomerCreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const [promoRes, profileRes, requestsRes, txRes] = await Promise.all([
        getMyPromotions(),
        getMe(),
        getMyCreditRequests(),
        getMyCreditTransactions(),
      ]);
      setPromotions(promoRes.data || []);
      setCredits(profileRes.data?.credits ?? 0);
      setCreditRequests(requestsRes.data || []);
      setTransactions(txRes.data || []);
    } catch {
      setPromotions([]);
      setCredits(null);
      setCreditRequests([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRequestCredits = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseInt(requestAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      setRequestError('Enter a valid positive amount.');
      return;
    }
    setRequestSubmitting(true);
    setRequestError(null);
    try {
      await createCreditRequest(amount);
      setRequestModalOpen(false);
      setRequestAmount('');
      await load();
    } catch (err: any) {
      setRequestError(err?.message || 'Failed to submit request.');
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handlePauseResume = async (p: Promotion) => {
    if (pausingId !== null) return;
    if (!p.is_active && credits !== null && credits < 0) {
      setError('Add more credits to continue');
      return;
    }
    setPausingId(p.id);
    setError(null);
    try {
      await updatePromotion(p.id, { is_active: p.is_active ? 0 : 1 });
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to update promotion');
    } finally {
      setPausingId(null);
    }
  };

  const cannotResume = credits !== null && credits < 0;

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = { story: 'Story', spotlight: 'Spotlight', category: 'Category' };
    return labels[type] || type;
  };

  const getApprovalLabel = (status?: string) => {
    if (!status || status === 'approved') return null;
    if (status === 'pending_approval') return { text: 'Pending approval', className: 'approval-pending' };
    return { text: 'Rejected', className: 'approval-rejected' };
  };


  return (
    <div className="customer-dashboard">
      <div className="dashboard-header">
        <h2>My Promotions</h2>
        <Link to="/customer/promotions" className="btn-add">
          + Create Promotion
        </Link>
      </div>

      {/* Credits: blue header + two panels side by side (scrollable, fixed height) */}
      <div className="credits-merged">
        <div className="credits-card credits-header">
          <span className="credits-label">Remaining credits</span>
          <span className="credits-value">
            {credits !== null ? Number(credits).toFixed(2) : '—'}
          </span>
          <button type="button" className="btn-request-credits" onClick={() => setRequestModalOpen(true)}>
            Request more credits
          </button>
        </div>
        <div className="credits-panels-row">
          <div className="credits-panel credit-requests-panel">
            <h3>My credit requests</h3>
            <div className="credits-panel-scroll">
              {creditRequests.length === 0 ? (
                <p className="credits-panel-empty">No requests yet.</p>
              ) : (
                <table className="credits-table credit-requests-table">
                  <thead>
                    <tr>
                      <th className="col-amount">Amount</th>
                      <th className="col-status">Status</th>
                      <th className="col-date">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditRequests.slice(0, 10).map((r) => (
                      <tr key={r.id}>
                        <td className="col-amount">{r.amount}</td>
                        <td className="col-status">
                          <span className={`req-status req-${r.status}`}>{r.status}</span>
                        </td>
                        <td className="col-date">{new Date(r.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div className="credits-panel credit-history-panel">
            <h3>Credit & burn history</h3>
            <div className="credits-panel-scroll">
              {transactions.length === 0 ? (
                <p className="credits-panel-empty">No transactions yet.</p>
              ) : (
                <table className="credits-table credit-history-table">
                  <thead>
                    <tr>
                      <th className="col-type">Type</th>
                      <th className="col-amount">Amount</th>
                      <th className="col-date">Date</th>
                      <th className="col-details">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => {
                      const promoTitle = t.promotion_id != null ? (promotions.find((p) => p.id === t.promotion_id)?.title ?? `Promotion #${t.promotion_id}`) : null;
                      const amountDisplay = t.type === 'deduction' ? `-${(Math.round(Math.abs(t.amount) * 100) / 100).toFixed(2)}` : `+${(Math.round(t.amount * 100) / 100).toFixed(2)}`;
                      const details = t.type === 'deduction' && promoTitle
                        ? `${promoTitle}${t.stat_parameter ? ` · ${t.stat_parameter}` : ''}`
                        : (t.type !== 'deduction' && t.notes ? t.notes : '—');
                      return (
                        <tr key={t.id}>
                          <td className="col-type">
                            <span className={`tx-type tx-${t.type}`}>{t.type.replace(/_/g, ' ')}</span>
                          </td>
                          <td className="col-amount">{amountDisplay}</td>
                          <td className="col-date">{new Date(t.created_at).toLocaleString()}</td>
                          <td className="col-details" title={t.stat_parameter ?? undefined}>{details}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {requestModalOpen && (
        <div className="modal-overlay" onClick={() => !requestSubmitting && setRequestModalOpen(false)}>
          <div className="modal-content request-credits-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Request credits</h3>
            <form onSubmit={handleRequestCredits}>
              <label>
                Amount
                <input
                  type="number"
                  min="1"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  placeholder="e.g. 100"
                  disabled={requestSubmitting}
                />
              </label>
              {requestError && <p className="form-error">{requestError}</p>}
              <div className="modal-actions">
                <button type="button" onClick={() => setRequestModalOpen(false)} disabled={requestSubmitting}>
                  Cancel
                </button>
                <button type="submit" disabled={requestSubmitting}>
                  {requestSubmitting ? 'Submitting…' : 'Submit request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading...</div>
      ) : promotions.length === 0 ? (
        <div className="empty-state">
          <p>You haven't created any promotions yet.</p>
          <Link to="/customer/promotions" className="btn-add">
            Create your first promotion
          </Link>
        </div>
      ) : (
        <>
          {cannotResume && promotions.some((p) => !p.is_active) && (
            <div className="credits-negative-banner" role="alert">
              Your balance is negative. Add more credits to continue and resume promotions.
            </div>
          )}
          {error && (
            <div className="credits-error-banner" role="alert">
              {error}
            </div>
          )}
          <div className="promotions-grid">
          {promotions.map((p) => {
            const approval = getApprovalLabel(p.approval_status);
            return (
              <div key={p.id} className="promo-card">
                <div className="promo-card-header">
                  <h3>{p.title}</h3>
                  <div className="promo-badges">
                    {approval && <span className={`approval-badge ${approval.className}`}>{approval.text}</span>}
                    <span className={`type-badge type-${p.promotion_type}`}>{getTypeLabel(p.promotion_type)}</span>
                  </div>
                </div>
                {p.brand_name && <p className="promo-brand">{p.brand_name}</p>}
                <p className="promo-dates">
                  {new Date(p.valid_from).toLocaleDateString()} – {new Date(p.valid_to).toLocaleDateString()}
                </p>
                {(p as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.length ? (
                  <p className="promo-pricing">
                    {((p as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices ?? [])
                      .map((s) => `${s.stat_parameter} ${s.price_per_unit}`)
                      .join(' · ')}
                  </p>
                ) : null}
                <div className="promo-card-actions">
                  <button
                    type="button"
                    className={p.is_active ? 'btn-pause' : 'btn-resume'}
                    onClick={() => handlePauseResume(p)}
                    disabled={pausingId !== null || (!p.is_active && cannotResume)}
                    title={!p.is_active && cannotResume ? 'Add more credits to continue' : p.is_active ? 'Pause promotion' : 'Resume promotion'}
                  >
                    {pausingId === p.id ? '…' : p.is_active ? 'Pause' : 'Resume'}
                  </button>
                  <Link to={`/customer/promotions?edit=${p.id}`} className="btn-edit">
                    Edit
                  </Link>
                  <Link to="/customer/statistics" className="btn-stats">
                    View Stats
                  </Link>
                </div>
              </div>
            );
          })}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerDashboard;
